import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { useDatabaseContent } from '@databases/database-state';
import { logError } from '@databases/logs';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { applyTemplate } from '../apply';
import { storage as createWithTemplateStorage } from '../storage';
import { type SystemTemplateType } from '../types';

/**
 * Applies templates that come from confluence quick insert actions, but could be used for other "system" level actions.
 * Would just need to update how createWithTemplateStorage is being used
 */
export const useApplySystemTemplate = () => {
  const intl = useIntl();
  const { contentId } = useDatabaseContent();
  const collabManager = collabStateManager.getCollabManager(contentId);
  const client = useAnalytics();

  const isEmptyDatabase = Boolean(collabManager?.database.isEmpty);

  const handleSendAnalyticsEvent = useCallback(
    (templateType: SystemTemplateType) => {
      void client.sendSystemTemplateApplied({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        templateType,
      });
    },
    [client, contentId]
  );

  const handleOnUpdate = useCallback(
    (templateType: SystemTemplateType) => {
      handleSendAnalyticsEvent(templateType);
    },
    [handleSendAnalyticsEvent]
  );

  const apply = useCallback(
    (templateType: SystemTemplateType) => {
      applyTemplate({ templateType, collabManager, intl })
        .then(() => {
          collabManager?.database.doc.on('update', () => handleOnUpdate(templateType));
        })
        .catch(logError);
    },
    [collabManager, handleOnUpdate, intl]
  );

  useEffect(() => {
    const templateType = createWithTemplateStorage.getTemplateType();
    if (
      isEmptyDatabase &&
      templateType &&
      isFeatureFlagEnabled('applyDatabaseTemplateFromQuickInsert')
    ) {
      apply(templateType);
    }
  }, [apply, isEmptyDatabase]);
};
